import { Ref, useMemo, useState, type FC } from "react";
import { Grid, Group, Stack, UnstyledButton } from "@mantine/core";
import Text from "@/components/atoms/Text";
import { useTranslation } from "react-i18next";
import { getContractTradeType } from "@/utils/Contracts";
import { useAuth } from "@/contexts/AuthContext";
import ContractTradeTypeEnum from "@/constants/enums/ContractTradeTypeEnum";
import { zodResolver } from "@hookform/resolvers/zod";
import { Controller, useForm } from "react-hook-form";
import { LocationAndDeliveryFormInput, getLocationAndDeliverySchema } from "../validation/locationAndDelivery";
import { findDeliveryAddressObject, formatAddress } from "@/utils/AddressHelper";
import TextInput from "@/components/atoms/TextInput";
import SelectCountry from "@/components/molecules/SelectInputs/SelectCountry";
import Divider from "@/components/atoms/Divider";
import SelectProductBoxType from "@/components/molecules/SelectInputs/SelectProductBoxType";
import SelectShippingAddress from "@/components/molecules/SelectInputs/SelectShippingAddress";
import DatePicker from "@/components/molecules/DatePicker";
import CoordinateFields from "@/components/molecules/CoordinateFields";
import { Contract } from "@/types";
import { determineIfEstimatedDeliveryIsRequired } from "@/components/organisms/CreateOrderFromContract/helpers";
import { useGetCompanyById, useGetCompanyByIdPublic } from "@/services/Company";
import DeliverySlotPicker from "@/components/organisms/DeliverySlotPicker";
import dayjs from "dayjs";

interface Props {
  onValidationSuccess: (data: any) => void;
  onChangeWizardData: (changed: any) => void;
  contract: Contract;
  ref?: Ref<HTMLFormElement>;
}

const LocationAndDeliveryStep: FC<Props & { ref?: React.Ref<HTMLFormElement> }> = ({
  onValidationSuccess,
  onChangeWizardData,
  contract,
  ref,
}) => {
  const { t } = useTranslation();
  const { currentCompany } = useAuth();

  const [slotPickerOpen, setSlotPickerOpen] = useState(false);

  const { data: company } = useGetCompanyById(currentCompany?.companyId as number, {
    enabled: !!currentCompany?.companyId,
  });

  const contractTradeType = getContractTradeType(contract!, currentCompany);

  // The receiver (the company that has the reception) is always the buyer
  const receiverCompanyId = contract?.contractBuyers[0]?.id;
  const fractionId = contract?.contractProduct?.companySpecificProduct?.salesItemId ?? null;

  const { data: receiverCompany } = useGetCompanyByIdPublic(receiverCompanyId as number, {
    enabled: !!receiverCompanyId,
  });

  const receiverHasOrderPlanning = receiverCompany?.isOrderPlanningEnabled ?? false;

  const isDeliveryTimeRequired = determineIfEstimatedDeliveryIsRequired(
    company?.isOrderPlanningEnabled ?? true,
    contract.automaticSalesApproval
  );

  // Prefer the address that was actually selected when the contract was created; only fall back
  // to the company's current default shipping address if the contract has none set.
  const ownAddress = useMemo(() => {
    const contractAddress =
      contractTradeType === ContractTradeTypeEnum.BUY ? contract?.transportToAddress : contract?.transportFromAddress;
    if (contractAddress?.address) return contractAddress;
    const companyAddress =
      contractTradeType === ContractTradeTypeEnum.BUY
        ? contract?.contractBuyers[0].companyAddress
        : contract?.contractSellers[0].companyAddress;
    return findDeliveryAddressObject(companyAddress ?? []);
  }, [contract, contractTradeType]);

  const counterpartAddress = useMemo(() => {
    const contractAddress =
      contractTradeType === ContractTradeTypeEnum.BUY ? contract?.transportFromAddress : contract?.transportToAddress;
    if (contractAddress?.address) return contractAddress;
    const companyAddress =
      contractTradeType === ContractTradeTypeEnum.BUY
        ? contract?.contractSellers[0].companyAddress
        : contract?.contractBuyers[0].companyAddress;
    return findDeliveryAddressObject(companyAddress ?? []);
  }, [contract, contractTradeType]);

  const contractAddressDefaults = useMemo(
    () => ({
      address: ownAddress?.address ?? "",
      postCode: ownAddress?.postCode ?? "",
      countryId: ownAddress?.country.id,
      place: ownAddress?.place ?? "",
    }),
    [ownAddress]
  );

  const { handleSubmit, control, watch, setValue } = useForm<LocationAndDeliveryFormInput>({
    resolver: zodResolver(getLocationAndDeliverySchema(isDeliveryTimeRequired, receiverHasOrderPlanning)),
    defaultValues: {
      packaging: 1,
      differentAddress: false,
      address: contractAddressDefaults.address,
      postCode: contractAddressDefaults.postCode,
      countryId: contractAddressDefaults.countryId,
      place: contractAddressDefaults.place,
      estimatedDelivery: undefined,
      latitude: "",
      longitude: "",
      capturedAt: null,
      receptionSlotId: null,
    },
  });

  const onSubmit = async (data: LocationAndDeliveryFormInput) => {
    onChangeWizardData(data);
    onValidationSuccess(data);
  };

  return (
    <form ref={ref} onSubmit={handleSubmit(onSubmit)}>
      <Grid>
        <Grid.Col span={{ base: 12, md: 6 }}>
          <Stack justify="flex-start">
            {/* YOUR COMPANY */}
            <Group p={0} gap={0} bg="transparent" align="center">
              <Text color="secondary" size="sm" data-testid={"company-deliver-label"}>
                {contractTradeType === ContractTradeTypeEnum.BUY
                  ? t("contracts:deliverTo")
                  : t("contracts:deliverFrom")}
              </Text>
              <UnstyledButton
                onClick={() => setValue("differentAddress", !watch("differentAddress"))}
                data-testid={"change-address-btn"}
              >
                <Text
                  ml={10}
                  color="secondary"
                  size="xs"
                  td="underline"
                  style={{
                    cursor: "pointer",
                    color: "var(--color-text-secondary)",
                  }}
                  data-testid={"change-address-btn-text"}
                >
                  {t("actions:changeAddress")}
                </Text>
              </UnstyledButton>
            </Group>
            {ownAddress && (
              <Text size="lg" strong={true} data-testid={"company-deliver-address"}>
                {formatAddress(ownAddress)}
              </Text>
            )}
          </Stack>
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 6 }}>
          {/* THEIR COMPANY */}
          <Stack>
            <Group p={0} gap={0} bg="transparent" align="center">
              <Text color="secondary" size="sm" data-testid={"counterpart-deliver-label"}>
                {contractTradeType === ContractTradeTypeEnum.BUY
                  ? t("contracts:deliverFrom")
                  : t("contracts:deliverTo")}
              </Text>
            </Group>
            {counterpartAddress && (
              <Text size="lg" strong={true} data-testid={"counterpart-deliver-address"}>
                {formatAddress(counterpartAddress)}
              </Text>
            )}
          </Stack>
        </Grid.Col>

        <Grid.Col span={12}>
          <Divider />
        </Grid.Col>
        {watch("differentAddress") && (
          <Grid.Col span={12}>
            <SelectShippingAddress
              companyId={currentCompany?.companyId}
              label={t("common:shippingAddress")}
              placeholder={t("common:shippingAddress")}
              autoSelectDefault={false}
              setSelectedAddress={(address) => {
                setValue("address", address.address, { shouldValidate: true, shouldDirty: true });
                setValue("postCode", address.postCode, { shouldValidate: true, shouldDirty: true });
                setValue("place", address.place, { shouldValidate: true, shouldDirty: true });
                setValue("countryId", address.country.id, { shouldValidate: true, shouldDirty: true });
              }}
            />
          </Grid.Col>
        )}
        <Grid.Col span={{ base: 12, md: 6 }}>
          <Controller
            control={control}
            name="address"
            render={({ field, fieldState }) => (
              <TextInput
                data-testid={"address-input"}
                label={t("orders:orderAddress")}
                disabled={!watch("differentAddress")}
                error={fieldState.error?.message ? t(fieldState.error.message) : undefined}
                {...field}
              />
            )}
          />
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 6 }}>
          <Controller
            control={control}
            name="countryId"
            render={({ field: { onChange, value }, fieldState }) => (
              <SelectCountry
                labelKey="name"
                valueKey="id"
                data-testid={"country-select"}
                label={t("common:country")}
                disabled={!watch("differentAddress")}
                value={value?.toString()}
                onChange={(val) => onChange(val ? Number(val) : null)}
                error={fieldState.error?.message ? t(fieldState.error.message) : undefined}
              />
            )}
          />
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 6 }}>
          <Controller
            control={control}
            name="postCode"
            render={({ field, fieldState }) => (
              <TextInput
                id="postCode-order-from-contract"
                data-testid={"postCode-input"}
                label={t("orders:postCode")}
                disabled={!watch("differentAddress")}
                error={fieldState.error?.message ? t(fieldState.error.message) : undefined}
                {...field}
              />
            )}
          />
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 6 }}>
          <Controller
            control={control}
            name="place"
            render={({ field, fieldState }) => (
              <TextInput
                id="postPlace"
                data-testid={"place-input"}
                label={t("orders:place")}
                disabled={!watch("differentAddress")}
                error={fieldState.error?.message ? t(fieldState.error.message) : undefined}
                {...field}
              />
            )}
          />
        </Grid.Col>

        <Grid.Col span={12}>
          <CoordinateFields
            control={control}
            latitudeName="latitude"
            longitudeName="longitude"
            setValue={setValue}
            onCaptured={(capturedAt) => setValue("capturedAt", capturedAt)}
          />
        </Grid.Col>
        <Grid.Col span={12}>
          <Divider />
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 6 }}>
          <Controller
            control={control}
            name="packaging"
            render={({ field: { onChange, value } }) => (
              <SelectProductBoxType
                id={"boxType-select"}
                labelKey="description"
                valueKey="id"
                data-testid={"boxType-select"}
                label={t("product:packaging")}
                value={value.toString()}
                onChange={onChange}
              />
            )}
          />
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 6 }}>
          <Controller
            control={control}
            name="estimatedDelivery"
            render={({ field: { onChange, value }, fieldState }) => {
              return (
                <DatePicker
                  testId="estimated-delivery"
                  label={t("contracts:estimatedDelivery")}
                  required={isDeliveryTimeRequired && !receiverHasOrderPlanning}
                  disabled={!contract.automaticSalesApproval || receiverHasOrderPlanning}
                  error={fieldState.error?.message ? t(fieldState.error.message) : undefined}
                  value={value ?? null}
                  onChange={onChange}
                  showTimePicker
                />
              );
            }}
          />
        </Grid.Col>

        {receiverHasOrderPlanning && (
          <Grid.Col span={12}>
            <Divider />
          </Grid.Col>
        )}

        {receiverHasOrderPlanning && (
          <Grid.Col span={{ base: 12, md: 6 }}>
            <Controller
              control={control}
              name="receptionSlotId"
              render={({ field: { value }, fieldState }) => (
                <TextInput
                  label={t("orders:deliveryTimeSlot")}
                  required
                  readOnly
                  value={
                    value
                      ? (() => {
                          const deliveryDate = watch("estimatedDelivery");
                          return deliveryDate
                            ? dayjs(deliveryDate as Date).format("DD.MM.YYYY HH:mm")
                            : t("orders:chooseDeliveryTimeSlot");
                        })()
                      : t("orders:chooseDeliveryTimeSlot")
                  }
                  error={fieldState.error?.message ? t(fieldState.error.message) : undefined}
                  onClick={() => setSlotPickerOpen(true)}
                  styles={{ input: { cursor: "pointer" } }}
                  data-testid="delivery-time-slot-input"
                />
              )}
            />
          </Grid.Col>
        )}
      </Grid>

      {receiverHasOrderPlanning && (
        <DeliverySlotPicker
          opened={slotPickerOpen}
          receiverCompanyId={receiverCompanyId as number}
          fractionId={fractionId}
          onClose={() => setSlotPickerOpen(false)}
          onConfirm={(slot) => {
            setValue("receptionSlotId", slot.id);
            setValue("estimatedDelivery", new Date(`${slot.slotDate}T${slot.startTime}`));
            setSlotPickerOpen(false);
          }}
        />
      )}
    </form>
  );
};

export default LocationAndDeliveryStep;
